import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getReferrals, ReferralData, ReferredUser } from '../api/referrals';
import { useNotificationStore } from '../store/notificationStore';
import { Button } from '../ui/components/Button';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { FeatherCopy, FeatherCheck, FeatherUsers, FeatherShare2, FeatherDollarSign } from '@subframe/core';
import { formatAddress, formatPrice } from '../utils/formatters';
import { MintReferralsTab } from '../components/referrals/MintReferralsTab';

type ReferralTab = 'account' | 'mint';

export const ReferralsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<ReferralTab>(
    searchParams.get('tab') === 'mint' ? 'mint' : 'account'
  );
  const [referralData, setReferralData] = useState<ReferralData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [copied, setCopied] = useState(false);

  const { success: showSuccess } = useNotificationStore();

  useEffect(() => {
    const fetchReferrals = async () => {
      try {
        setIsLoading(true);
        const data = await getReferrals();
        setReferralData(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch referral data'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchReferrals();
  }, []);

  const handleCopyLink = async () => {
    if (!referralData) return;

    try {
      await navigator.clipboard.writeText(referralData.referral_link);
      setCopied(true);
      showSuccess('Referral link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (isLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full flex-col items-start">
          <div className="container max-w-none flex w-full grow shrink-0 basis-0 flex-col items-center gap-4 bg-default-background py-12">
            <div className="text-center py-12">Loading referral data...</div>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  if (error) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full flex-col items-start">
          <div className="container max-w-none flex w-full grow shrink-0 basis-0 flex-col items-center gap-4 bg-default-background py-12">
            <div className="flex w-full max-w-[768px] flex-col items-start gap-4">
              <div className="w-full bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                Failed to load referral data: {error.message}
              </div>
            </div>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-8 pb-12">
        {/* Page Header */}
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-heading-1 font-heading-1 text-default-font">
            Referrals
          </span>
          <span className="text-body font-body text-subtext-color">
            Earn rewards by referring users and promoting launches
          </span>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-4 border-b border-neutral-border w-full">
          {(['account', 'mint'] as ReferralTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setSearchParams(tab === 'account' ? {} : { tab: 'mint' }, { replace: true });
              }}
              className={`px-4 py-3 text-body font-body border-b-2 -mb-px transition-colors ${
                activeTab === tab
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-subtext-color hover:text-default-font'
              }`}
            >
              {tab === 'account' ? 'Account Referrals' : 'Mint Referrals'}
            </button>
          ))}
        </div>

        {/* Mint Referrals Tab */}
        {activeTab === 'mint' && (
          <MintReferralsTab />
        )}

        {/* Account Referrals Tab */}
        {activeTab === 'account' && (
        <>

        {/* Rewards Info Section */}
        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex items-center gap-2">
            <FeatherDollarSign className="text-subtext-color" />
            <span className="text-caption font-caption text-subtext-color uppercase tracking-wider">
              EARN REWARDS
            </span>
          </div>

          <div className="w-full rounded-lg border border-brand-200 bg-brand-50 p-6">
            <div className="flex flex-col gap-4">
              <p className="text-body font-body text-default-font">
                Earn a share of the revenue generated by the users you refer:
              </p>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-brand-600 text-white font-bold text-sm">
                    45%
                  </div>
                  <div className="flex flex-col">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Direct Referrals
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      Earn 45% of the revenue from users you directly refer
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-brand-400 text-white font-bold text-sm">
                    5%
                  </div>
                  <div className="flex flex-col">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Second-Level Referrals
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      Earn 5% of the revenue from your referrals' referrals
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Referral Link Section */}
        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex items-center gap-2">
            <FeatherShare2 className="text-subtext-color" />
            <span className="text-caption font-caption text-subtext-color uppercase tracking-wider">
              YOUR REFERRAL LINK
            </span>
          </div>

          <div className="flex w-full flex-col items-start gap-4">
            <p className="text-body font-body text-subtext-color">
              Share this link with friends. When they sign up using your link, they'll be attributed to you.
            </p>

            <div className="flex w-full items-center gap-3">
              <div className="flex-1 flex items-center gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3 overflow-hidden">
                <span className="flex-1 text-body font-body text-default-font truncate">
                  {referralData?.referral_link}
                </span>
              </div>
              <Button
                variant="neutral-secondary"
                icon={copied ? <FeatherCheck className="text-green-600" /> : <FeatherCopy />}
                onClick={handleCopyLink}
              >
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
            </div>

            <div className="flex items-center gap-2 text-caption text-subtext-color">
              <span>Your referral code:</span>
              <code className="px-2 py-1 bg-neutral-100 rounded text-sm font-mono">
                {referralData?.referral_code}
              </code>
            </div>
          </div>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Stats Section */}
        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex items-center gap-2">
            <FeatherUsers className="text-subtext-color" />
            <span className="text-caption font-caption text-subtext-color uppercase tracking-wider">
              YOUR REFERRALS & EARNINGS
            </span>
          </div>

          <div className="w-full grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-lg border border-neutral-border bg-neutral-50 p-6">
              <div className="flex flex-col items-center gap-2">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {referralData?.referral_count || 0}
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  {referralData?.referral_count === 1 ? 'User Referred' : 'Users Referred'}
                </span>
              </div>
            </div>
            <div className="rounded-lg border border-neutral-border bg-neutral-50 p-6">
              <div className="flex flex-col items-center gap-2">
                <span className="text-heading-2 font-heading-2 text-brand-600">
                  {referralData?.total_first_level_earnings ? formatPrice(referralData.total_first_level_earnings) : '0 XRP'}
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  First-Level Earnings
                </span>
              </div>
            </div>
            <div className="rounded-lg border border-neutral-border bg-neutral-50 p-6">
              <div className="flex flex-col items-center gap-2">
                <span className="text-heading-2 font-heading-2 text-brand-400">
                  {referralData?.total_second_level_earnings ? formatPrice(referralData.total_second_level_earnings) : '0 XRP'}
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  Second-Level Earnings
                </span>
              </div>
            </div>
            <div className="rounded-lg border border-brand-200 bg-brand-50 p-6">
              <div className="flex flex-col items-center gap-2">
                <span className="text-heading-2 font-heading-2 text-brand-700">
                  {referralData?.total_earnings ? formatPrice(referralData.total_earnings) : '0 XRP'}
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  Total Earnings
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Referred Users Table */}
        {referralData && referralData.referred_users.length > 0 && (
          <>
            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

            <div className="flex w-full flex-col items-start gap-6">
              <span className="text-caption font-caption text-subtext-color uppercase tracking-wider">
                REFERRED WALLETS
              </span>

              <div className="w-full overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-neutral-border">
                      <th className="text-left py-3 px-4 text-caption font-caption text-subtext-color uppercase tracking-wider">
                        User
                      </th>
                      <th className="text-left py-3 px-4 text-caption font-caption text-subtext-color uppercase tracking-wider">
                        Joined
                      </th>
                      <th className="text-right py-3 px-4 text-caption font-caption text-subtext-color uppercase tracking-wider">
                        First-Level Earnings
                      </th>
                      <th className="text-right py-3 px-4 text-caption font-caption text-subtext-color uppercase tracking-wider">
                        Second-Level Earnings
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {referralData.referred_users.map((user: ReferredUser) => (
                      <tr
                        key={user.id}
                        className="border-b border-neutral-border hover:bg-neutral-50 transition-colors"
                      >
                        <td className="py-3 px-4">
                          <Link
                            to={`/wallets/${user.xrpl_address}`}
                            className="flex items-center gap-3"
                          >
                            {/* Avatar */}
                            <div className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-brand-100 overflow-hidden">
                              {user.avatar_url ? (
                                <img
                                  src={user.avatar_url}
                                  alt=""
                                  className="h-8 w-8 object-cover"
                                />
                              ) : (
                                <span className="text-xs font-medium text-brand-600">
                                  {user.username?.[0]?.toUpperCase() || user.xrpl_address[0]}
                                </span>
                              )}
                            </div>
                            {/* User Info */}
                            <div className="flex flex-col min-w-0">
                              <span className="text-body-bold font-body-bold text-default-font truncate hover:text-brand-600">
                                {user.username || formatAddress(user.xrpl_address)}
                              </span>
                              {user.username && (
                                <span className="text-caption font-caption text-subtext-color truncate">
                                  {formatAddress(user.xrpl_address)}
                                </span>
                              )}
                            </div>
                          </Link>
                        </td>
                        <td className="py-3 px-4 text-body font-body text-subtext-color whitespace-nowrap">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 text-right text-body font-body text-brand-600 whitespace-nowrap">
                          {user.first_level_earnings ? formatPrice(user.first_level_earnings) : '0 XRP'}
                        </td>
                        <td className="py-3 px-4 text-right text-body font-body text-brand-400 whitespace-nowrap">
                          {user.second_level_earnings ? formatPrice(user.second_level_earnings) : '0 XRP'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Empty State */}
        {referralData && referralData.referred_users.length === 0 && (
          <>
            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

            <div className="w-full flex flex-col items-center gap-4 py-8 text-center">
              <FeatherUsers className="w-12 h-12 text-neutral-400" />
              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  No referrals yet
                </span>
                <span className="text-body font-body text-subtext-color max-w-md">
                  Share your referral link with friends to get started. When they sign up, they'll appear here.
                </span>
              </div>
            </div>
          </>
        )}

        </>
        )}
      </div>
    </DefaultPageLayout>
  );
};
